import { useCallback, useEffect, useState } from "react";

export interface SectionPageResult<T> {
  candidates: T[];
  total: number;
  total_pages: number;
}

/** Pagina uma seção (um status) de forma independente das demais — cada
 * seção mantém sua própria página, loading e requisição, ao invés de uma
 * paginação global compartilhada e depois recortada por status no front. */
export function useSectionPage<T>(
  fetcher: (page: number) => Promise<SectionPageResult<T>>,
  /** Deps que, ao mudar, resetam a seção pra página 1 (ex.: busca, filtros). */
  resetDeps: unknown[],
  enabled = true,
) {
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<SectionPageResult<T> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback((silent = false) => {
    if (!enabled) return;
    if (!silent) setIsLoading(true);
    setError(false);
    fetcher(page)
      .then(setResult)
      .catch(() => setError(true))
      .finally(() => { if (!silent) setIsLoading(false); });
  }, [fetcher, page, enabled]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, resetDeps);
  useEffect(() => { load(); }, [load]);

  return {
    page,
    setPage,
    candidates: result?.candidates ?? [],
    total: result?.total ?? 0,
    totalPages: result?.total_pages ?? 1,
    isLoading,
    error,
    /** true a partir da 1ª resposta — usado pra nunca mostrar "sem candidatos"
     * antes da seção sequer ter sido buscada. */
    hasFetched: result !== null,
    refetch: load,
  };
}
